// MyDashboardPage.tsx

import { useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  List,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Assignment,
  Timer,
  CheckCircleOutlineOutlined,
  ExpandMore,
  Book,
  Language,
  TrendingDown,
  InfoOutlined,
  Gavel,
  History,
  Analytics,
  Speed,
} from "@mui/icons-material";

// --- Type Definitions ---
interface LocalizedText {
  en: string;
  "zh-hk": string;
}

interface SelectionMatrix {
  option: string;
  timeConsumed: number;
  updatedTimes: number;
}

interface QuestionResponse {
  id: string;
  question: LocalizedText;
  option: Record<string, LocalizedText>;
  correctOption: string;
  page: number;
  source: string;
  explanation: LocalizedText;
  selected: {
    option: string;
    optionText: LocalizedText;
  };
  selectionMatrices: SelectionMatrix[];
  questionText: LocalizedText;
  timeConsumed: number;
  updatedTimes: number;
  isCorrect: boolean;
}

interface QuizAttempt {
  _id: { $oid: string };
  submissionId: string;
  userId: string;
  quiz: {
    id: string;
    name: string;
    paper: string;
    section: number;
    totalQuestions: number;
  };
  isCompleted: boolean;
  attemptNumber: number;
  responses: QuestionResponse[];
  totalTimeConsumed: number;
  createdAt: { $date: string };
  updatedAt: { $date: string };
}

// --- Historical Array Mock Data ---
const historicalQuizData: QuizAttempt[] = [
  {
    _id: { $oid: "6a43305fc4c6fcb9e4cfedf2" },
    submissionId: "6f376986-1aa8-4805-82bc-55b342aa3718",
    userId: "6a1172ed5fff00f368ae2975",
    quiz: {
      id: "hkma-aml-cft-guidelines-placeholder-id",
      name: "hkmaaml",
      paper: "HKMA AML CFT Guidelines",
      section: 1,
      totalQuestions: 5,
    },
    isCompleted: true,
    attemptNumber: 1,
    responses: [
      {
        id: "6a1e2bcc64c85d2a7497ac7d",
        question: {
          en: "What may a failure to comply with any provision of this Guideline reflect adversely on?",
          "zh-hk": "未能遵守本指引的任何規定可能對什麼產生不利影響？",
        },
        option: {
          A: {
            en: "Whether an AI continues to comply with the authorization criteria set out in the Seventh Schedule to the BO",
            "zh-hk": "認可機構是否繼續遵守BO第七附表所列的認可準則",
          },
          B: { en: "Whether the AI has sufficient capital reserves", "zh-hk": "認可機構是否有足夠的資本儲備" },
          C: {
            en: "Whether the AI's employees have received adequate training",
            "zh-hk": "認可機構的員工是否接受了足夠的培訓",
          },
          D: { en: "Whether the AI maintains appropriate office premises", "zh-hk": "認可機構是否維持適當的辦公場所" },
        },
        correctOption: "A",
        page: 5,
        source:
          "A failure to comply with any provision of this Guideline may reflect adversely on whether an AI continues to comply with the authorization criteria set out in the Seventh Schedule to the BO, particularly paragraph 10...",
        explanation: {
          en: "The text explicitly states that failure to comply may reflect adversely on whether an AI continues to comply with authorization criteria.",
          "zh-hk": "文本明確指出，未能遵守可能對認可機構是否繼續遵守BO第七附表中認可準則產生不利影響。",
        },
        selected: {
          option: "A",
          optionText: { en: "Whether an AI continues to comply...", "zh-hk": "認可機構是否繼續遵守..." },
        },
        selectionMatrices: [{ option: "A", timeConsumed: 2, updatedTimes: 1 }],
        questionText: {
          en: "What may a failure to comply with any provision of this Guideline reflect adversely on?",
          "zh-hk": "未能遵守本指引的任何規定可能對什麼產生不利影響？",
        },
        timeConsumed: 2,
        updatedTimes: 1,
        isCorrect: true,
      },
      {
        id: "6a1e2bcc64c85d2a7497abf2",
        question: {
          en: 'What does the use of the words "must" or "should" in this Guideline indicate?',
          "zh-hk": "本指引中使用「必須」或「應該」等詞語表示什麼？",
        },
        option: {
          A: { en: "Optional recommendations that AIs may choose to follow", "zh-hk": "認可機構可選擇遵守的可選建議" },
          B: { en: "Mandatory requirements that AIs must comply with", "zh-hk": "認可機構必須遵守的強制性要求" },
          C: { en: "Suggestions that are only applicable to certain types of AIs", "zh-hk": "僅適用於某些類型認可機構的建議" },
          D: { en: "Best practices that are encouraged but not required", "zh-hk": "鼓勵但非必需的最佳實踐" },
        },
        correctOption: "B",
        page: 4,
        source:
          'the use of the word "must" or "should" in relation to an action... indicates that it is a mandatory requirement',
        explanation: {
          en: 'The text clearly states that "must" or "should" indicates mandatory requirements.',
          "zh-hk": "文本明確指出「必須」或「應該」表示強制性要求。",
        },
        selected: { option: "A", optionText: { en: "Optional recommendations...", "zh-hk": "認可機構可選擇遵守的可選建議" } },
        selectionMatrices: [{ option: "A", timeConsumed: 1, updatedTimes: 1 }],
        questionText: {
          en: 'What does the use of the words "must" or "should" in this Guideline indicate?',
          "zh-hk": "本指引中使用「必須」或「應該」等詞語表示什麼？",
        },
        timeConsumed: 1,
        updatedTimes: 1,
        isCorrect: false,
      },
      {
        id: "6a1e2bcc64c85d2a7497ac7f",
        question: {
          en: "Under what circumstances is this Guideline admissible in evidence in proceedings under the AMLO before any court?",
          "zh-hk": "在根據 AMLO 進行的任何法院訴訟中，本指引在何種情況下可被採納為證據？",
        },
        option: {
          A: { en: "Only when submitted by the defendant", "zh-hk": "僅在被告提交時" },
          B: { en: "Only when submitted by the HKMA", "zh-hk": "僅在HKMA提交時" },
          C: { en: "When any party submits it as evidence", "zh-hk": "當任何一方將其作為證據提交時" },
          D: {
            en: "It is always admissible as it is an official regulatory document",
            "zh-hk": "作為官方監管文件，它始終可被接納",
          },
        },
        correctOption: "C",
        page: 5,
        source: "any proceedings under the AMLO before any court, this Guideline is admissible in evidence...",
        explanation: {
          en: "The text indicates the Guideline is admissible in evidence when submitted by any party.",
          "zh-hk": "文本指出，在根據AMLO進行的任何法院訴訟中，當任何一方提交時，本指引可被採納為證據。",
        },
        selected: { option: "A", optionText: { en: "Only when submitted by the defendant", "zh-hk": "僅在被告提交時" } },
        selectionMatrices: [{ option: "A", timeConsumed: 0, updatedTimes: 1 }],
        questionText: {
          en: "Under what circumstances is this Guideline admissible in evidence in proceedings under the AMLO before any court?",
          "zh-hk": "在根據 AMLO 進行的任何法院訴訟中，本指引在何種情況下可被採納為證據？",
        },
        timeConsumed: 0,
        updatedTimes: 1,
        isCorrect: false,
      },
      {
        id: "6a1e2bcc64c85d2a7497ae4e",
        question: {
          en: "What does the OSCO create in relation to the proceeds of indictable offences?",
          "zh-hk": "OSCO 對可公訴罪行的收益制定了什麼規定？",
        },
        option: {
          A: { en: "A civil recovery procedure", "zh-hk": "民事追討程序" },
          B: { en: "An offence of money laundering", "zh-hk": "洗錢罪行" },
          C: { en: "A regulatory framework for financial institutions", "zh-hk": "金融機構監管框架" },
          D: { en: "An international cooperation treaty", "zh-hk": "國際合作條約" },
        },
        correctOption: "B",
        page: 7,
        source: "creates an offence of ML in relation to the proceeds of indictable offences",
        explanation: {
          en: "The document explicitly states that OSCO creates an offence of money laundering.",
          "zh-hk": "文件明確指出，OSCO 就可公訴罪行的收益制定了洗錢罪行。",
        },
        selected: { option: "A", optionText: { en: "A civil recovery procedure", "zh-hk": "民事追討程序" } },
        selectionMatrices: [{ option: "A", timeConsumed: 1, updatedTimes: 1 }],
        questionText: {
          en: "What does the OSCO create in relation to the proceeds of indictable offences?",
          "zh-hk": "OSCO 對可公訴罪行的收益制定了什麼規定？",
        },
        timeConsumed: 1,
        updatedTimes: 1,
        isCorrect: false,
      },
      {
        id: "6a1e2bcc64c85d2a7497ae54",
        question: {
          en: "What type of offences does the OSCO enable courts to receive information about for determining appropriate sentencing?",
          "zh-hk": "OSCO 允許法院就哪些類型罪行接收信息以決定適當判刑？",
        },
        option: {
          A: { en: "Only minor traffic offences", "zh-hk": "僅輕微交通罪行" },
          B: { en: "Only white-collar financial crimes", "zh-hk": "僅白領金融犯罪" },
          C: {
            en: "Organised crime/triad related offences or other serious offences",
            "zh-hk": "有組織犯罪/三合會相關罪行或其他嚴重罪行",
          },
          D: { en: "Only immigration violations", "zh-hk": "僅出入境違規" },
        },
        correctOption: "C",
        page: 7,
        source:
          "enables the Courts... to receive information about an offender... where the offence amounts to an organised crime/triad related offence or other serious offences",
        explanation: {
          en: "The document explicitly states that courts can receive information about organised crime/triad related offences.",
          "zh-hk": "文件明確指出，法院可以就判刑目的接收有組織犯罪/三合會相關罪行或其他嚴重罪行的信息。",
        },
        selected: { option: "A", optionText: { en: "Only minor traffic offences", "zh-hk": "僅輕微交通罪行" } },
        selectionMatrices: [{ option: "A", timeConsumed: 0, updatedTimes: 1 }],
        questionText: {
          en: "What type of offences does the OSCO enable courts to receive information about for determining appropriate sentencing?",
          "zh-hk": "OSCO 允許法院就哪些類型罪行接收信息以決定適當判刑？",
        },
        timeConsumed: 0,
        updatedTimes: 1,
        isCorrect: false,
      },
    ],
    totalTimeConsumed: 4,
    createdAt: { $date: "2026-06-30T02:56:31.543Z" },
    updatedAt: { $date: "2026-06-30T02:56:31.543Z" },
  },
];

export const MyDashboardPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [lang, setLang] = useState<"en" | "zh-hk">("en");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>(historicalQuizData[0].submissionId);

  // 1. Find the active selected record
  const activeAttempt = useMemo(() => {
    return historicalQuizData.find((attempt) => attempt.submissionId === selectedSubmissionId) || historicalQuizData[0];
  }, [selectedSubmissionId]);

  // 2. Aggregate metrics across the entire array (Historic Overview Metrics)
  const aggregateMetrics = useMemo(() => {
    const totalAttempts = historicalQuizData.length;
    const scores = historicalQuizData.map((a) => (a.responses.filter((r) => r.isCorrect).length / a.quiz.totalQuestions) * 100);
    const averageHistoricalScore = scores.reduce((a, b) => a + b, 0) / totalAttempts;

    // Active current calculations
    const activeTotal = activeAttempt.quiz.totalQuestions;
    const activeCorrect = activeAttempt.responses.filter((r) => r.isCorrect).length;
    const activeScorePercentage = (activeCorrect / activeTotal) * 100;
    const activeAvgPacePerQuestion = activeAttempt.totalTimeConsumed / activeTotal;

    return {
      totalAttempts,
      averageHistoricalScore,
      activeCorrect,
      activeTotal,
      activeScorePercentage,
      activeAvgPacePerQuestion,
    };
  }, [activeAttempt]);

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh", py: 4, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {/* --- EXECUTIVE MANAGEMENT BAR --- */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.5px" }}>
              Compliance Matrix Audit Space
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reviewing Program: <strong>{activeAttempt.quiz.paper}</strong> (Section {activeAttempt.quiz.section})
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {/* Submission Dropdown Selector */}
            <FormControl
              size="small"
              sx={{ minWidth: 220, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <InputLabel id="submission-select-label">Select Attempt Version</InputLabel>
              <Select
                labelId="submission-select-label"
                value={selectedSubmissionId}
                label="Select Attempt Version"
                onChange={(e) => setSelectedSubmissionId(e.target.value)}
              >
                {historicalQuizData.map((attempt) => (
                  <MenuItem key={attempt.submissionId} value={attempt.submissionId}>
                    Attempt #{attempt.attemptNumber} ({new Date(attempt.createdAt.$date).toLocaleDateString()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={lang}
              exclusive
              onChange={(_, next) => next && setLang(next)}
              size="small"
              sx={{ bgcolor: "background.paper" }}
            >
              <ToggleButton value="en" sx={{ textTransform: "none", fontWeight: 600 }}>
                <Language sx={{ fontSize: 16, mr: 0.5 }} /> EN
              </ToggleButton>
              <ToggleButton value="zh-hk" sx={{ textTransform: "none", fontWeight: 600 }}>
                繁中
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* --- AGGREGATED HISTORIC STATS TILES --- */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Tile 1: Selected Attempt Accuracy */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.03)",
                borderTop: `4px solid ${theme.palette.primary.main}`,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Selected Accuracy
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main", width: 32, height: 32 }}
                  >
                    <Assignment sx={{ fontSize: 18 }} />
                  </Avatar>
                </Box>
                <Typography
                  variant="h4"
                  color={aggregateMetrics.activeScorePercentage >= 70 ? "success.main" : "error.main"}
                  sx={{ fontWeight: 700 }}
                >
                  {aggregateMetrics.activeScorePercentage}%
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={aggregateMetrics.activeScorePercentage}
                    color={aggregateMetrics.activeScorePercentage >= 70 ? "success" : "error"}
                    sx={{
                      borderRadius: 1,
                      height: 4,
                      bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : undefined,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Tile 2: Historic Metric Pool */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.03)",
                borderTop: `4px solid ${theme.palette.success.main}`,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Historical Records
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.success.main, 0.15), color: "success.main", width: 32, height: 32 }}
                  >
                    <History sx={{ fontSize: 18 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {aggregateMetrics.totalAttempts}{" "}
                  <span style={{ fontSize: "1rem", fontWeight: 400, color: theme.palette.text.secondary }}>Attempts</span>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Avg Benchmark Score: {aggregateMetrics.averageHistoricalScore.toFixed(0)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Tile 3: Processing Time */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.03)",
                borderTop: `4px solid ${theme.palette.warning.main}`,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total Duration
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.warning.main, 0.15), color: "warning.main", width: 32, height: 32 }}
                  >
                    <Timer sx={{ fontSize: 18 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {activeAttempt.totalTimeConsumed}{" "}
                  <span style={{ fontSize: "1rem", fontWeight: 400, color: theme.palette.text.secondary }}>mins</span>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Status: Completed Successfully
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Tile 4: Response Velocity */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.03)",
                borderTop: `4px solid ${theme.palette.error.main}`,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Velocity Pace
                  </Typography>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.main", width: 32, height: 32 }}>
                    <Speed sx={{ fontSize: 18 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {aggregateMetrics.activeAvgPacePerQuestion.toFixed(1)}{" "}
                  <span style={{ fontSize: "1rem", fontWeight: 400, color: theme.palette.text.secondary }}>m/q</span>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  High risk velocity anomalies tracked
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* --- RECONCILIATION RISK REPORT --- */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 1,
                bgcolor: isDark ? alpha(theme.palette.warning.main, 0.15) : "#fff3e0",
                border: `1px solid ${isDark ? alpha(theme.palette.warning.main, 0.3) : "#ffe0b2"}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TrendingDown color="warning" />
                  <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                    Anomalous Behavior Report
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
                  The user completed all {aggregateMetrics.activeTotal} regulatory compliance sections in just{" "}
                  <strong>{activeAttempt.totalTimeConsumed} minutes</strong>. This constitutes a warning flags for pacing
                  anomalies.
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Primary Failure Artifact:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Systemic patterns show continuous submission loops toward <strong>Option A</strong> on failure items.
                  Remediation target: Differentiate statutory requirements vs. optional policies.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 1,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 3, display: "flex", alignItems: "center", gap: 1, color: "text.primary" }}
                >
                  <Gavel color="primary" /> Regulatory Coverage & Risk Exposure Matrix
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 1,
                        borderColor: "divider",
                        bgcolor: "background.default",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
                        HKMA BO Schedule 7
                      </Typography>
                      <Chip
                        label="100% Secure"
                        color="success"
                        size="small"
                        sx={{ mt: 1, fontWeight: 700, borderRadius: 1 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 1,
                        borderColor: theme.palette.error.main,
                        bgcolor: "background.default",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
                        AMLO Statutory Evidence
                      </Typography>
                      <Chip
                        label="Breached / High Risk"
                        color="error"
                        size="small"
                        sx={{ mt: 1, fontWeight: 700, borderRadius: 1 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 1,
                        borderColor: theme.palette.error.main,
                        bgcolor: "background.default",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
                        OSCO Criminal Mandates
                      </Typography>
                      <Chip
                        label="Breached / High Risk"
                        color="error"
                        size="small"
                        sx={{ mt: 1, fontWeight: 700, borderRadius: 1 }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* --- DYNAMIC ATTEMPT RESPONSES TRACKER --- */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, mb: 2, color: "text.primary", display: "flex", alignItems: "center", gap: 1 }}
        >
          <Analytics /> Selected Version Audit Log (Attempt #{activeAttempt.attemptNumber})
        </Typography>

        {activeAttempt.responses.map((item, index) => (
          <Accordion
            key={item.id}
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: `${theme.shape.borderRadius}px !important`,
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.02)",
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `5px solid ${item.isCorrect ? theme.palette.success.main : theme.palette.error.main}`,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: "text.secondary" }} />}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  pr: 2,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.875rem",
                      bgcolor: item.isCorrect ? "success.main" : "error.main",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
                    {item.question[lang]}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    size="small"
                    icon={<Book sx={{ fontSize: "14px !important", color: "text.secondary" }} />}
                    label={`P. ${item.page}`}
                    variant="outlined"
                    sx={{ borderRadius: 1, borderColor: "divider", color: "text.secondary" }}
                  />
                  <Chip
                    size="small"
                    label={item.isCorrect ? "Passed" : "Failed"}
                    color={item.isCorrect ? "success" : "error"}
                    variant="filled"
                    sx={{ fontWeight: 700, borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, px: 3, pb: 3 }}>
              <Divider sx={{ mb: 2, borderColor: "divider" }} />

              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, mb: 1 }}>
                Options Breakdown Matrix:
              </Typography>
              <List disablePadding>
                {Object.entries(item.option).map(([key, value]) => {
                  const isSelected = item.selected.option === key;
                  const isCorrect = item.correctOption === key;

                  let borderStyle = `1px solid ${theme.palette.divider}`;
                  let bgStyle = "background.default";
                  if (isSelected) {
                    borderStyle = `1px solid ${theme.palette.error.main}`;
                    bgStyle = isDark ? alpha(theme.palette.error.main, 0.15) : "#ffebee";
                  }
                  if (isCorrect) {
                    borderStyle = `1px solid ${theme.palette.success.main}`;
                    bgStyle = isDark ? alpha(theme.palette.success.main, 0.15) : "#e8f5e9";
                  }

                  return (
                    <ListItem
                      key={key}
                      sx={{
                        border: borderStyle,
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: bgStyle,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <Typography
                          color={isCorrect ? "success.main" : isSelected ? "error.main" : "text.secondary"}
                          sx={{ fontWeight: 700 }}
                        >
                          {key}.
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={value[lang]}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            sx: {
                              fontWeight: isCorrect || isSelected ? 700 : 400,
                              color: "text.primary",
                            },
                          },
                        }}
                      />
                      {isCorrect && (
                        <Chip
                          size="small"
                          label="Correct Target"
                          color="success"
                          sx={{ ml: 1, height: 20, borderRadius: 1 }}
                        />
                      )}
                      {isSelected && !isCorrect && (
                        <Chip
                          size="small"
                          label="User Selection"
                          color="error"
                          sx={{ ml: 1, height: 20, borderRadius: 1 }}
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>

              {/* Individual Question Metadata Matrix */}
              <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                    <Timer sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      Time Consumed: <strong>{item.timeConsumed} seconds</strong>
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                    <CheckCircleOutlineOutlined sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      Selection Cycles: <strong>{item.updatedTimes} update(s)</strong>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Explanatory Core */}
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <InfoOutlined fontSize="small" color="primary" /> Explanation:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {item.explanation[lang]}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    borderLeft: `3px solid ${theme.palette.divider}`,
                    pl: 1,
                  }}
                >
                  <strong>Statutory Provision Source:</strong> "{item.source}"
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </Box>
  );
};
